import {
  ServiceWithPricing,
  QuoteFullyPopulated,
} from '@/util/types/serverTypes';

/**
 * Applies price overrides to calculated services
 * 
 * Handles two scenarios:
 * 1. First calculation with quote: Initialize overrides from quote charges
 * 2. Subsequent calculations: Preserve manual price overrides from previous calculation
 * 
 * This ensures that when users manually adjust prices, those adjustments
 * persist across recalculations triggered by other changes
 * 
 * @param calculatedServices - Services with freshly calculated prices
 * @param previousServices - Services from previous calculation (to preserve overrides)
 * @param quote - Quote data (for initial override values)
 * @returns Services with price overrides applied
 */
export function applyPriceOverrides(
  calculatedServices: ServiceWithPricing[],
  previousServices?: ServiceWithPricing[],
  quote?: QuoteFullyPopulated
): ServiceWithPricing[] {
  // First calculation with quote - initialize from quote
  if (!previousServices || previousServices.length === 0) {
    if (!quote) return calculatedServices;

    return calculatedServices.map((newService) => {
      const quoteService = quote.charges.find(
        (p) => p._serviceId?._id === newService._id
      );
      if (quoteService && quoteService.amount !== undefined) {
        return {
          ...newService,
          priceOverride:
            quoteService.amount !== newService.totalPrice
              ? quoteService.amount
              : undefined,
          totalPrice: quoteService.amount,
          chargeId: quoteService._id,
        };
      }
      return newService;
    });
  }

  // Subsequent calculations - preserve overrides from previous
  return calculatedServices.map((newService) => {
    const prevService = previousServices.find((p) => p._id === newService._id);
    if (prevService && prevService.priceOverride !== undefined) {
      return {
        ...newService,
        priceOverride: prevService.priceOverride,
        totalPrice: prevService.priceOverride,
        chargeId: prevService.chargeId,
      };
    }
    return { ...newService, chargeId: prevService?.chargeId };
  });
}

